import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppResources } from '@app/app.resources';
import { ConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { NewHireQuestionsTable } from '@app/modules/employees/services';
import { EmployeeModuleMenuService } from '@app/modules/employees/services/employee-module-menu.service';
import { DefaultWelcomeMessageForm, OnboardingQuestionForm } from '@app/modules/forms/employees/components';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { MenuColumnItem } from '@app/modules/table/interfaces/menu-column-item.interface';
import VideoLookupComponent from '@app/modules/videos/components/video-lookup/video-lookup.component';
import VideoPreviewComponent from '@app/modules/videos/components/video-preview/video-preview.component';
import { VideoMetadata } from '@app/modules/videos/services/types';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { IndexView } from '@common/views';
import { OnboardingReminderTable } from '@employees/services/tables/onboarding-reminder/onboarding-reminder.table';
import { BaseForm } from '@forms/base.form';
import { OnboardingReminderForm } from '@forms/employees/components/onboarding-reminder/onboarding-reminder.form';
import { HireQuestion } from '@models/employee/hire-question.model';
import { OnboardingOption } from '@models/employee/onboarding-option.model';
import { OnboardingReminder } from '@models/employee/onboarding-reminder.model';
import { CompanySetting } from '@models/settings/company-setting.model';
import Video from '@models/videos/video.model';

@Component({
    templateUrl: './onboarding-index.template.html',
    styleUrls: ['./onboarding-index.style.scss'],
})
export class OnboardingIndexView extends IndexView implements OnInit {
    @ViewChild('confirmationDialog') confirmationDialog!: ConfirmationDialogComponent;
    @ViewChild('editReminderForm') editReminderForm!: OnboardingReminderForm;
    @ViewChild('editQuestionForm') editQuestionForm!: OnboardingQuestionForm;
    @ViewChild('videoLookup') videoLookup?: VideoLookupComponent;
    @ViewChild(VideoPreviewComponent) videoPreview?: VideoPreviewComponent;

    currentlyOpenDialog?:
        | 'CREATE_REMINDER'
        | 'EDIT_REMINDER'
        | 'CREATE_QUESTION'
        | 'EDIT_QUESTION'
        | 'DEFAULT_VIDEO'
        | 'DEFAULT_WELCOME_MESSAGE' = undefined;

    isLoading = true;
    options?: OnboardingOption;
    settings: {
        bankingInformationRequired?: CompanySetting;
        bankingInformationOnboarding?: CompanySetting;
        sendHiringQuestionnaireToNewHires?: CompanySetting; // TODO deprecated
        sendWelcomeMessageToNewHires?: CompanySetting; // TODO deprecated
    } = {};
    onboardingVideoEnabled = false;
    onboardingVideoMetadata?: VideoMetadata;
    onboardingVideoUrl?: string;
    isUpdatingOnboardingVideo = false;

    constructor(
        private http: HttpClient,
        private notify: NotifyService,
        private auth: AuthService,
        public table: OnboardingReminderTable,
        public newHireQuestionTable: NewHireQuestionsTable,
        protected featureService: FeatureService,
        protected analyticsService: AnalyticService,
        public employeeModuleMenuService: EmployeeModuleMenuService,
        private router: Router
    ) {
        super();
        this.newHireQuestionTable.reload();
    }

    onCreateOnboardingReminder(form: OnboardingReminderForm): void {
        if (!this.isFormValid(form)) {
            return;
        }

        const reminder = form.reminder;

        this.isLoading = true;

        if (reminder.remindBefore === null) {
            reminder.remindBefore = true;
            reminder.delayDays = 0;
        }

        if (reminder.remindTo === 'colleague') {
            reminder.remindTo = reminder.colleagueId;
        }

        reminder
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.closeDialog();
                this.notify.success('saved');
                this.isLoading = false;
                form.reset();
                this.table.reload();
            })
            .catch(() => {
                this.isLoading = false;
                this.notify.error('employees.onboardingSettings.unableToCreateReminder');
            });
    }

    onCreateOnboardingQuestion(form: OnboardingQuestionForm): void {
        if (!this.isFormValid(form)) {
            return;
        }

        this.isLoading = true;

        form.hireQuestion
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.closeDialog();
                this.notify.success('saved');
                this.newHireQuestionTable.reload();
                this.isLoading = false;
                form.reset();
            })
            .catch(() => this.notify.error('employees.onboardingSettings.unableToCreateQuestion'));
    }

    ngOnInit(): void {
        this.table.reload();
        Promise.all([
            OnboardingOption.param('company', this.auth.company.id).with(['defaultOnboardingVideo']).all(),
            CompanySetting.param('company', this.auth.company.id)
                .where('module', 'EmployeeManagement')
                .with('setting')
                .get(),
            this.featureService.has(FeatureFlag.onboardingVideo),
        ]).then(([[onboardingOption], [companySettings], onboardingVideoEnabled]) => {
            this.options = onboardingOption && onboardingOption.length ? onboardingOption[0] : new OnboardingOption();
            this.registerCompanySettings(companySettings);
            this.onboardingVideoEnabled = onboardingVideoEnabled;
            this.isLoading = false;
        });
    }

    closeDialog(): void {
        this.currentlyOpenDialog = undefined;
    }

    saveDefaultWelcomeMessage(form: DefaultWelcomeMessageForm): void {
        if (!this.isFormValid(form) || !this.options) {
            return;
        }

        const defaultWelcomeMessage = form.defaultWelcomeMessage;

        this.isLoading = true;
        this.options.defaultWelcomeMessage = defaultWelcomeMessage ?? null;
        this.options
            .param('company', this.auth.company.id)
            .save()
            .then((options) => {
                this.options = options;
                this.closeDialog();
                this.notify.success('employees.onboardingSettings.hiringSettingsSaved');
                this.isLoading = false;
            });
    }

    editQuestion(form: OnboardingQuestionForm): void {
        if (!this.isFormValid(form)) {
            return;
        }

        form.hireQuestion
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.notify.success('Question saved');
                this.newHireQuestionTable.reload();
            })
            .catch((err) => {
                this.notify.error(err.message || 'employees.onboardingSettings.unableToSaveQuestion');
            });
    }

    deleteQuestion(question: HireQuestion): void {
        this.confirmationDialog.open().then((res) => {
            if (res) {
                this.isLoading = true;
                question
                    .param('company', this.auth.company.id)
                    .delete()
                    .then(() => {
                        this.newHireQuestionTable.reload();
                        this.isLoading = false;
                        this.notify.success('employees.onboardingSettings.questionWasDeleted');
                    })
                    .catch((err) => {
                        this.notify.error(err.message || 'employees.onboardingSettings.questionCouldNotBeDeleted');
                        this.isLoading = false;
                    });
            }
        });
    }

    editReminder(form: OnboardingReminderForm): void {
        if (!this.isFormValid(form)) {
            return;
        }

        form.reminder
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.notify.success('employees.onboardingSettings.reminderSaved');
                this.table.reload();
            })
            .catch((err) => this.notify.error(err.message || 'employees.onboardingSettings.unableToSaveReminder'));
    }

    deleteReminder(reminder: OnboardingReminder): void {
        this.confirmationDialog.open().then((res) => {
            if (res) {
                this.isLoading = true;
                reminder
                    .param('company', this.auth.company.id)
                    .delete()
                    .then(() => {
                        this.table.reload();
                        this.notify.success('employees.onboardingSettings.onboardingReminderWasDeleted');
                        this.isLoading = false;
                    })
                    .catch((err) => {
                        this.notify.error(
                            err.message || 'employees.onboardingSettings.onboardingReminderCouldNotBeDeleted'
                        );
                        this.isLoading = false;
                    });
            }
        });
    }

    onSettingChanged(model: CompanySetting): void {
        model
            .param('company', this.auth.company.id)
            .save()
            .catch((err) => this.notify.error(err.message || 'employees.onboardingSettings.couldNotSaveSetting'));
    }

    sendAccountInvites(): void {
        this.isLoading = true;
        this.getEmployeeAccountIdsForOnboarding()
            .then((accountIds) => {
                this.http
                    .post(
                        AppResources.BulkInvite.replace(':company', this.auth.company.id.toString()),
                        this.getBulkInvitePayload(accountIds)
                    )
                    .toPromise()
                    .then(() => {
                        this.onSendAccountInvitesSuccess();
                        this.analyticsService.trackEvent(AnalyticEvents.PeopleSettingsOnboardingResendInvites);
                    })
                    .catch(() => this.onSendAccountInvitesFailure());
            })
            .catch((err: Error) => this.onAccountIdsForOnboardingFailure(err));
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        const reminder = data as OnboardingReminder;
        switch (menuItem.event) {
            case 'delete':
                this.deleteReminder(reminder);
                break;
            case 'edit':
                this.editReminderForm.setReminder(reminder);
                this.currentlyOpenDialog = 'EDIT_REMINDER';
                break;
        }
    }

    newHireQuestionMenuAction(event: { menuItem: MenuColumnItem; data?: HireQuestion }): void {
        const { menuItem, data: hireQuestion } = event;

        if (!hireQuestion) {
            return;
        }

        switch (menuItem.event) {
            case 'delete':
                this.deleteQuestion(hireQuestion);
                break;
            case 'edit':
                this.editQuestionForm.hireQuestion = hireQuestion;
                this.currentlyOpenDialog = 'EDIT_QUESTION';
                break;
        }
    }

    openDefaultVideoModal(): void {
        this.onboardingVideoUrl = this.options?.defaultOnboardingVideo?.url;
        this.currentlyOpenDialog = 'DEFAULT_VIDEO';
    }

    async saveDefaultVideo(form?: VideoLookupComponent): Promise<void> {
        if (!this.isFormValid(form) || !this.options || !form) {
            return;
        }

        // No changes to video just close the dialog
        if (this.onboardingVideoMetadata?.videoId === form?.videoMetadata?.videoId) {
            this.closeDialog();
            return;
        }

        this.onboardingVideoMetadata = form?.videoMetadata;
        form.isLoading = true;
        this.isLoading = true;

        if (this.onboardingVideoMetadata) {
            const video = new Video({ url: this.onboardingVideoMetadata.url });

            await video.save();

            this.options.defaultOnboardingVideo = video;
        } else {
            this.options.defaultOnboardingVideo = null;
        }

        this.options
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.isLoading = false;
                this.notify.success('employees.onboardingSettings.saved');
            })
            .catch(() => {
                this.notify.error('employees.onboardingSettings.error');
                this.isLoading = false;
            })
            .finally(() => {
                this.closeDialog();
            });
    }

    editBankingInfo(): void {
        this.router.navigate(['/employees', 'settings', 'onboarding', 'bankingInfo']);
    }

    private registerCompanySettings(companySettings: CompanySetting[]): void {
        const bankingInformationRequired = companySettings.find(
            (setting) => setting.setting.name === 'bankingInformation.required'
        );
        const bankingInformationOnboarding = companySettings.find(
            (setting) => setting.setting.name === 'bankingInformation.collectDuringOnboarding'
        );
        const sendHiringQuestionnaireToNewHires = companySettings.find(
            (setting) => setting.setting.name === 'sendHiringQuestionnaireToNewHiresByDefault'
        );
        const sendWelcomeMessageToNewHires = companySettings.find(
            (setting) => setting.setting.name === 'sendWelcomeMessageToNewHiresByDefault'
        );

        this.settings = {
            bankingInformationRequired,
            bankingInformationOnboarding,
            sendHiringQuestionnaireToNewHires,
            sendWelcomeMessageToNewHires,
        };

        // Show error if registering fails
        if (
            !(
                this.settings.bankingInformationRequired &&
                this.settings.bankingInformationOnboarding &&
                this.settings.sendHiringQuestionnaireToNewHires &&
                this.settings.sendWelcomeMessageToNewHires
            )
        ) {
            this.notify.error('employees.onboardingSettings.couldNotRegisterCompanySettings');
        }
    }

    private onSendAccountInvitesSuccess(): void {
        this.isLoading = false;
        this.notify.success('employees.onboardingSettings.accountInvitationsSent');
    }

    private onSendAccountInvitesFailure(): void {
        this.isLoading = false;
        this.notify.error('employees.onboardingSettings.accountInvitationsCouldNotBeSent');
    }

    private onAccountIdsForOnboardingFailure(err: Error): void {
        this.isLoading = false;
        this.notify.error(err.message || 'employees.onboardingSettings.couldNotLookupAccountsForInvite');
    }

    private getBulkInvitePayload(accountIds: number[]): object {
        return {
            data: {
                type: 'InviteRequest',
                attributes: {
                    accountIds: accountIds,
                },
            },
        };
    }
    private getEmployeeAccountIdsForOnboarding(): Promise<number[]> {
        return new Promise<number[]>((resolve, reject) => {
            Employee.param('company', this.auth.company.id)
                .where('status', 'onboarding')
                .all()
                .then(([employees]) => {
                    const accountIds = employees.map((employee) => +employee.accountId);
                    if (!accountIds.length) {
                        this.notify.info('employees.onboardingSettings.noOnboardingEmployeesToInvite');
                        this.isLoading = false;
                        return;
                    }
                    resolve(accountIds);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    private isFormValid(form?: BaseForm): boolean {
        if (!form) {
            return false;
        }

        form.submit();

        return form.valid;
    }
}
